import React, { useEffect } from "react";
import useIsGtmLoaded from "./useIsGtmLoaded";
import { sendGtmEvent } from "~/analytics/definitions";

const sendProductAdEvent = (
  eventAction: string,
  productAdId: string,
  productAdName: string,
) => {
  sendGtmEvent({
    event: "Product_Ad_Event",
    eventAction,
    productAdId: Number(productAdId),
    eventLabel: productAdName,
  });
};

function handleClick(event: Event) {
  if (event.currentTarget instanceof HTMLElement) {
    const adId = event.currentTarget.id;
    const adName = getAdNameFromAnchorTag(event.currentTarget);
    if (!adId || !adName) return;
    sendProductAdEvent("Click", adId, adName);
  }
}

function getAdNameFromAnchorTag(container: Element) {
  const rawHtmlAnchorTag = container.querySelector("a");
  return rawHtmlAnchorTag?.getAttribute("id");
}

const useProductAdTracking = (containerRef: React.RefObject<HTMLElement>) => {
  const [gtmStatus] = useIsGtmLoaded();

  useEffect(() => {
    let adContainerNodes: NodeListOf<Element> | null;
    let productAdObserver: IntersectionObserver;
    if (containerRef.current) {
      adContainerNodes = containerRef.current?.querySelectorAll(`.adContainer`);
      if (gtmStatus && adContainerNodes) {
        adContainerNodes.forEach((node) => {
          node.addEventListener("click", handleClick);
        });
        productAdObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const adId = entry.target.getAttribute("id");
                const adName = getAdNameFromAnchorTag(entry.target);
                if (!adId || !adName) return;
                sendProductAdEvent("Impression", adId, adName);
                productAdObserver.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.8 },
        );
        adContainerNodes.forEach((node) => {
          if (node) {
            productAdObserver.observe(node);
          }
        });
      }
    }
    function cleanup() {
      adContainerNodes?.forEach((node) => {
        node.removeEventListener("click", handleClick);
      });
      productAdObserver?.disconnect();
    }
    return cleanup;
  }, [containerRef, gtmStatus]);
};

export default useProductAdTracking;
